/**
 * RoleDetailPanel — the ONE "what can this role do?" panel (2026-08-19).
 *
 * Extracted from InviteModal so the onboarding invite step and the invite
 * modal render role permissions IDENTICALLY: grouped by catalog category
 * (human labels), custom/legacy keys in an "other" bucket, honest count in
 * the header. Progressive disclosure by design — hosts mount it behind an
 * expander/ⓘ so the zero-decision path stays clean.
 */

import React, { useMemo } from 'react';
import styled from 'styled-components';
import { PERMISSION_CATALOG_UI } from '../../constants/permissionCatalog';

export interface RoleDetailPanelRole {
  name: string;
  description?: string;
  effectivePermissions?: string[];
  grants?: string[];
}

/** Group a role's permission keys by catalog category (human labels);
 *  anything outside the catalog (custom.* / legacy) lands in "other". */
export function groupPermissionsByCategory(
  keys: string[],
): Array<{ label: string; items: string[] }> {
  const perms = new Set(keys);
  const groups: Array<{ label: string; items: string[] }> = [];
  for (const cat of PERMISSION_CATALOG_UI) {
    const items = cat.permissions.filter((p) => perms.has(p.key)).map((p) => p.label);
    if (items.length) groups.push({ label: cat.label, items });
    cat.permissions.forEach((p) => perms.delete(p.key));
  }
  if (perms.size) groups.push({ label: 'other', items: Array.from(perms) });
  return groups;
}

const Panel = styled.div`
  border: 1px solid var(--color-border-focus, var(--brand-gold));
  background: var(--color-accent-subtle, rgba(198, 167, 94, 0.08));
  border-radius: var(--radius-lg, 12px);
  padding: 0.875rem 1rem;
  max-height: 300px;
  overflow-y: auto;

  @media (max-width: 860px) {
    max-height: 240px;
  }
`;

const Title = styled.div`
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--color-text-primary, var(--brand-text));
  text-transform: lowercase;
  margin-bottom: 0.25rem;
`;

const Desc = styled.div`
  font-size: 0.75rem;
  color: var(--color-text-secondary, #5A5346);
  line-height: 1.4;
  margin-bottom: 0.375rem;
`;

const Category = styled.div`
  font-size: 0.625rem;
  font-weight: 700;
  color: var(--color-text-tertiary, var(--brand-text-muted));
  text-transform: lowercase;
  letter-spacing: 0.06em;
  margin: 0.375rem 0 0.125rem;
`;

const Chip = styled.span`
  display: inline-block;
  font-size: 0.6875rem;
  color: var(--color-accent-text, #6b5d3f);
  background: var(--color-background-primary, #fff);
  border: 1px solid var(--color-border-light, #ECEAE5);
  border-radius: var(--radius-pill, 999px);
  padding: 0.05rem 0.45rem;
  margin: 0.1rem 0.2rem 0.1rem 0;
`;

export const RoleDetailPanel: React.FC<{ role: RoleDetailPanelRole }> = ({ role }) => {
  const perms = role.effectivePermissions ?? role.grants ?? [];
  const groups = useMemo(() => groupPermissionsByCategory(perms), [perms]);
  return (
    <Panel data-testid="role-detail-panel">
      <Title>
        {role.name} — {perms.length} permission{perms.length !== 1 ? 's' : ''}
      </Title>
      {role.description && <Desc>{role.description}</Desc>}
      {groups.length === 0 && <Desc>this role has no permissions yet.</Desc>}
      {groups.map((group) => (
        <div key={group.label}>
          <Category>{group.label}</Category>
          <div>
            {group.items.map((item) => (
              <Chip key={item}>{item}</Chip>
            ))}
          </div>
        </div>
      ))}
    </Panel>
  );
};

export default RoleDetailPanel;
